/*
 * assets/kcc-header.js
 * Behaviour for sections/header.liquid — the Liquid port of KCCNavbar.tsx.
 *
 * Replaces this React state:
 *   scrolled   (window.scrollY > 40)  -> bg-kcc-forest + shadow-lg vs bg-transparent
 *   shopOpen   (hover on a nav item)  -> dropdown visibility + aria-expanded
 *   mobileOpen (hamburger)            -> full-screen menu, focus trap, Escape, body lock
 *   NavCartBridge.count               -> cart badge, refreshed from 'kcc:cart:updated'
 *   NavCartBridge.toggle              -> cancelable 'kcc:cart:toggle' on document
 *
 * Contract with assets/kcc-cart.js (owned by the cart drawer):
 *   listens  document 'kcc:cart:updated'  detail = the /cart.js JSON (reads detail.item_count)
 *   emits    document 'kcc:cart:toggle'   cancelable; call preventDefault() to keep the shopper
 *                                         on the page, otherwise the link falls through to /cart.
 *                                         (kcc-cart.js instead binds [data-kcc-cart-toggle]
 *                                         directly and preventDefaults the click itself — both
 *                                         paths are supported.)
 *   listens  document 'kcc:cart:open' / 'kcc:cart:close' (and the -ed spellings) -> aria-expanded
 *
 * Every class this file adds or removes is listed in the Tailwind safelist comment at the top
 * of sections/header.liquid, because the Tailwind build only scans .liquid files.
 *
 * Vanilla JS, no dependencies.
 */
(function () {
  'use strict';

  /* The section tag loads this file; guard against a second <script src> executing it twice. */
  if (window.KCCHeaderLoaded) return;
  window.KCCHeaderLoaded = true;

  var FOCUSABLE =
    'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), iframe, [tabindex]:not([tabindex="-1"])';

  var DESKTOP_QUERY = '(min-width: 768px)'; /* Tailwind md */

  var instances = [];

  function qsa(root, selector) {
    return Array.prototype.slice.call(root.querySelectorAll(selector));
  }

  function isEscape(event) {
    return event.key === 'Escape' || event.key === 'Esc';
  }

  function canHover() {
    return !window.matchMedia || window.matchMedia('(hover: hover)').matches;
  }

  function isVisible(el) {
    return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
  }

  /* ------------------------------------------------------------------ instance */

  function createHeader(root) {
    var nav = root.querySelector('[data-kcc-nav]');
    var menu = root.querySelector('[data-kcc-mobile-menu]');
    var menuToggle = root.querySelector('[data-kcc-menu-toggle]');
    var bars = qsa(root, '[data-kcc-bar]');
    var cartToggles = qsa(root, '[data-kcc-cart-toggle]');
    var badges = qsa(root, '[data-kcc-cart-count]');
    var overlay = root.getAttribute('data-kcc-overlay') === 'true';

    var threshold = parseInt(root.getAttribute('data-kcc-scroll-threshold'), 10);
    if (isNaN(threshold)) threshold = 40;

    var scrolled = null;
    var menuOpen = false;
    var lastFocused = null;

    /* -- scrolled -------------------------------------------------------- */

    function applyScroll() {
      if (!nav) return;
      /* Non-overlay templates are solid from the first paint; nothing to toggle. */
      if (!overlay) return;

      var next = window.pageYOffset > threshold;
      if (next === scrolled) return;
      scrolled = next;

      nav.classList.toggle('bg-kcc-forest', next);
      nav.classList.toggle('shadow-lg', next);
      nav.classList.toggle('bg-transparent', !next);
      nav.setAttribute('data-kcc-scrolled', next ? 'true' : 'false');
    }

    /* -- dropdowns ------------------------------------------------------- */

    var dropdowns = qsa(root, '[data-kcc-dropdown]').map(function (item) {
      var trigger = item.querySelector('[data-kcc-dropdown-trigger]');
      var panel = item.querySelector('[data-kcc-dropdown-panel]');
      var open = false;

      function setOpen(next) {
        if (!panel || open === next) return;
        open = next;
        panel.classList.toggle('invisible', !next);
        panel.classList.toggle('opacity-0', !next);
        panel.classList.toggle('visible', next);
        panel.classList.toggle('opacity-100', next);
        if (trigger) trigger.setAttribute('aria-expanded', next ? 'true' : 'false');
      }

      /* Hover, like the TSX onMouseEnter / onMouseLeave. */
      item.addEventListener('mouseenter', function () {
        setOpen(true);
      });
      item.addEventListener('mouseleave', function () {
        setOpen(false);
      });

      /* Keyboard: open while focus is anywhere inside the item. */
      item.addEventListener('focusin', function () {
        setOpen(true);
      });
      item.addEventListener('focusout', function (event) {
        if (!item.contains(event.relatedTarget)) setOpen(false);
      });

      /* Touch / no-hover pointers: first tap opens, second tap follows the link.
         Without this the submenu is unreachable on tablets at the md breakpoint. */
      if (trigger) {
        trigger.addEventListener('click', function (event) {
          if (canHover()) return;
          if (!open) {
            event.preventDefault();
            setOpen(true);
          }
        });
      }

      return {
        element: item,
        close: function () {
          setOpen(false);
        },
        focusTrigger: function () {
          if (trigger) trigger.focus();
        },
        isOpen: function () {
          return open;
        }
      };
    });

    function closeDropdowns(except) {
      dropdowns.forEach(function (dropdown) {
        if (dropdown !== except) dropdown.close();
      });
    }

    /* -- mobile menu ----------------------------------------------------- */

    function focusables() {
      if (!menu) return [];
      var list = qsa(menu, FOCUSABLE).filter(isVisible);
      if (menuToggle) list.unshift(menuToggle);
      return list;
    }

    function setMenu(next) {
      if (!menu || !menuToggle || menuOpen === next) return;
      menuOpen = next;

      menu.classList.toggle('opacity-0', !next);
      menu.classList.toggle('pointer-events-none', !next);
      menu.classList.toggle('opacity-100', next);
      menu.classList.toggle('pointer-events-auto', next);
      menu.setAttribute('aria-hidden', next ? 'false' : 'true');

      if (next) {
        menu.removeAttribute('inert');
      } else {
        menu.setAttribute('inert', '');
      }

      menuToggle.setAttribute('aria-expanded', next ? 'true' : 'false');

      /* Hamburger -> X, same transforms as the TSX. */
      bars.forEach(function (bar) {
        var index = bar.getAttribute('data-kcc-bar');
        if (index === '1') {
          bar.classList.toggle('rotate-45', next);
          bar.classList.toggle('translate-y-[5px]', next);
        } else if (index === '2') {
          bar.classList.toggle('opacity-0', next);
        } else if (index === '3') {
          bar.classList.toggle('-rotate-45', next);
          bar.classList.toggle('-translate-y-[5px]', next);
        }
      });

      document.body.classList.toggle('overflow-hidden', next);

      if (next) {
        lastFocused = document.activeElement;
        menu.focus();
      } else if (lastFocused && typeof lastFocused.focus === 'function') {
        lastFocused.focus();
        lastFocused = null;
      }
    }

    if (menuToggle) {
      menuToggle.addEventListener('click', function () {
        setMenu(!menuOpen);
      });
    }

    if (menu) {
      /* The TSX closes the menu on link click; navigation usually does it for us,
         but not for in-page anchors. */
      menu.addEventListener('click', function (event) {
        var link = event.target.closest ? event.target.closest('a[href]') : null;
        if (link) setMenu(false);
      });

      /* Focus trap. */
      menu.addEventListener('keydown', function (event) {
        if (event.key !== 'Tab' || !menuOpen) return;
        var list = focusables();
        if (!list.length) return;

        var first = list[0];
        var last = list[list.length - 1];
        var active = document.activeElement;

        if (event.shiftKey && (active === first || active === menu)) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && active === last) {
          event.preventDefault();
          first.focus();
        }
      });
    }

    /* The hamburger is the first stop in the trap, so Shift+Tab off it has to wrap to the
       last link instead of escaping into the page behind the drawer. */
    if (menuToggle) {
      menuToggle.addEventListener('keydown', function (event) {
        if (event.key !== 'Tab' || !menuOpen || !event.shiftKey) return;
        var list = focusables();
        if (!list.length) return;
        event.preventDefault();
        list[list.length - 1].focus();
      });
    }

    /* -- cart ------------------------------------------------------------ */

    function setCount(count) {
      badges.forEach(function (badge) {
        badge.textContent = count;
        if (count > 0) {
          badge.hidden = false;
          badge.style.display = '';
        } else {
          badge.hidden = true;
          badge.style.display = 'none';
        }
      });
    }

    function setCartExpanded(next) {
      cartToggles.forEach(function (toggle) {
        toggle.setAttribute('aria-expanded', next ? 'true' : 'false');
      });
    }

    cartToggles.forEach(function (toggle) {
      toggle.addEventListener('click', function (event) {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;

        var request = new CustomEvent('kcc:cart:toggle', {
          bubbles: true,
          cancelable: true,
          detail: { source: 'header' }
        });
        document.dispatchEvent(request);

        /* Drawer answered -> stay put. Nothing listening (no JS cart yet) -> go to /cart. */
        if (request.defaultPrevented) event.preventDefault();
      });
    });

    applyScroll();

    return {
      root: root,
      applyScroll: applyScroll,
      closeDropdowns: closeDropdowns,
      dropdowns: dropdowns,
      closeMenu: function () {
        setMenu(false);
      },
      isMenuOpen: function () {
        return menuOpen;
      },
      setCount: setCount,
      setCartExpanded: setCartExpanded
    };
  }

  /* ------------------------------------------------------------------- global */

  function initAll() {
    instances = qsa(document, '[data-kcc-header-root]').map(function (root) {
      if (root.kccHeader) return root.kccHeader;
      var instance = createHeader(root);
      root.kccHeader = instance;
      return instance;
    });

    /* A section reload while the menu was open would otherwise strand the body lock. */
    var anyOpen = instances.some(function (instance) {
      return instance.isMenuOpen();
    });
    if (!anyOpen) document.body.classList.remove('overflow-hidden');
  }

  var ticking = false;
  function onScroll() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(function () {
      ticking = false;
      instances.forEach(function (instance) {
        instance.applyScroll();
      });
    });
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });

  document.addEventListener('keydown', function (event) {
    if (!isEscape(event)) return;

    instances.forEach(function (instance) {
      instance.dropdowns.forEach(function (dropdown) {
        if (!dropdown.isOpen()) return;
        dropdown.close();
        if (dropdown.element.contains(document.activeElement)) dropdown.focusTrigger();
      });

      /* closeMenu() restores focus to the hamburger. */
      if (instance.isMenuOpen()) instance.closeMenu();
    });
  });

  /* Tap outside closes a submenu opened by touch. */
  document.addEventListener('click', function (event) {
    instances.forEach(function (instance) {
      instance.dropdowns.forEach(function (dropdown) {
        if (dropdown.isOpen() && !dropdown.element.contains(event.target)) dropdown.close();
      });
    });
  });

  /* Leaving the desktop breakpoint with the drawer open would strand the body lock. */
  if (window.matchMedia) {
    var desktop = window.matchMedia(DESKTOP_QUERY);
    var onBreakpoint = function (event) {
      if (!event.matches) return;
      instances.forEach(function (instance) {
        instance.closeMenu();
      });
    };
    if (desktop.addEventListener) {
      desktop.addEventListener('change', onBreakpoint);
    } else if (desktop.addListener) {
      desktop.addListener(onBreakpoint);
    }
  }

  /* Cart count, pushed by assets/kcc-cart.js after every /cart.js round trip. */
  document.addEventListener('kcc:cart:updated', function (event) {
    var detail = event.detail || {};
    var cart = detail.cart || detail;
    var count = cart.item_count;
    if (typeof count !== 'number') return;

    instances.forEach(function (instance) {
      instance.setCount(count);
    });
  });

  ['kcc:cart:open', 'kcc:cart:opened'].forEach(function (name) {
    document.addEventListener(name, function () {
      instances.forEach(function (instance) {
        instance.setCartExpanded(true);
      });
    });
  });

  ['kcc:cart:close', 'kcc:cart:closed'].forEach(function (name) {
    document.addEventListener(name, function () {
      instances.forEach(function (instance) {
        instance.setCartExpanded(false);
      });
    });
  });

  /* Theme editor. */
  document.addEventListener('shopify:section:load', initAll);
  document.addEventListener('shopify:section:unload', initAll);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
